import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useSnack } from "../providers/SnackBarProvider";
import { submitFeedback } from "./feedbackApi";
import { useDismissible } from "../native/dismissibleStack";
import { useKeyboardInset } from "../native/keyboardInset";

const CATEGORIES = [
  { value: "bug", label: "Bug", Icon: BugReportIcon },
  { value: "idea", label: "Idea", Icon: LightbulbIcon },
  { value: "praise", label: "Praise", Icon: FavoriteIcon },
  { value: "other", label: "Other", Icon: ChatBubbleOutlineIcon },
];

const MAX = 2000;

const FeedbackDialog = ({ open, onClose }) => {
  const setSnack = useSnack();
  const location = useLocation();
  // Android 15+ (API 36) draws edge-to-edge and no longer resizes the WebView
  // when the IME opens. This dialog is almost entirely one multiline field, it
  // autoFocuses — so the keyboard is up before the user has done anything — and
  // Send sits underneath it. See src/native/keyboardInset.js.
  const keyboardInset = useKeyboardInset();
  const [category, setCategory] = useState("idea");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCategory("idea");
    setMessage("");
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  // handleClose already no-ops while saving, so Back is swallowed mid-submit.
  useDismissible(open, handleClose, "feedback-dialog");

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSaving(true);
    try {
      await submitFeedback({ category, message: message.trim(), path: location.pathname });
      setSnack("success", "Thanks — your feedback was sent!");
      reset();
      onClose();
    } catch (err) {
      setSnack("error", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "20px" } }}
      sx={{
        // MUI centres the dialog inside a full-height container. Padding the
        // bottom of that container by the keyboard height shrinks the box the
        // dialog centres in, which lifts it by exactly half the inset — which is
        // exactly what re-centres it in the strip of screen the keyboard leaves
        // visible. It also shrinks what the Paper's own
        // `max-height: calc(100% - 64px)` resolves against, so a tall dialog
        // still fits. `box-sizing` is border-box app-wide via CssBaseline;
        // stated here because the arithmetic depends on it. 0 on web.
        "& .MuiDialog-container": {
          boxSizing: "border-box",
          pb: `${keyboardInset}px`,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>Send feedback</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Found a bug, have an idea, or just want to say hi? We read everything.
        </Typography>

        <ToggleButtonGroup
          value={category}
          exclusive
          onChange={(_, v) => v && setCategory(v)}
          fullWidth
          size="small"
          sx={{ mb: 2, flexWrap: "wrap", gap: 0.5, "& .MuiToggleButton-root": { border: "1px solid rgba(0,0,0,0.12)", borderRadius: "10px !important", flex: "1 0 40%" } }}
        >
          {CATEGORIES.map(({ value, label, Icon }) => (
            <ToggleButton key={value} value={value} sx={{ textTransform: "none", gap: 0.5 }}>
              <Icon fontSize="small" />
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX))}
          placeholder="Tell us what's on your mind…"
          inputProps={{ maxLength: MAX }}
        />
        <Box sx={{ textAlign: "right", mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {message.length}/{MAX}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ color: "#64748b" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !message.trim()}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? "Sending…" : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
